/*
 * Everything the UserDetail page derives from its six API responses.
 *
 * THE THREE HEADLINE NUMBERS COME FROM THREE DIFFERENT PIPELINES.
 *
 *   Total Tracked   summary.totalTime          sum of WebsiteAppUsage.totalTime
 *   Total Work      summary.totalWorkTime      sum of TimeEntry.duration
 *   Idle            summary.totalIdleDuration  sum of IdleTime.duration
 *
 * The old page — and html-markup, which copied its wording — told the reader
 * that "Total Work Time is a subset of Total Tracked Time". It is not. The
 * desktop runs ActivityService and TimeEntryService independently (see
 * CLAUDE.md, "Two independent tracking pipelines"), so the two numbers are
 * measured separately and can disagree in EITHER direction: usage exceeds work
 * when several app records overlap, work exceeds usage when a usage upload
 * failed while the session kept running. Presenting one as contained in the
 * other turns an ordinary disagreement into a number the reader will not trust
 * once they notice it — so this file reports the relationship it measures
 * instead of asserting one.
 */
import { PRODUCTIVITY } from "@/lib/tokens.js";

const HOUR = 3_600_000;

/* ---- The three hero numbers -------------------------------------------- */

export function heroStats(summary = {}, usageStats = [], browsers = []) {
    const tracked = summary.totalTime ?? 0;
    const work = summary.totalWorkTime ?? 0;
    const idle = summary.totalIdleDuration ?? 0;

    const apps = usageStats.filter((u) => u.type === "application").length;
    const sites = usageStats.filter((u) => u.type === "website" || u.type === "domain").length;

    return {
        tracked,
        work,
        idle,
        apps,
        sites,
        browsers: browsers.length,
        idlePeriods: summary.idlePeriods ?? null,

        /*
         * How the two independent measurements actually compare, as a
         * percentage of the larger one — so the caption can say "work is 94% of
         * tracked" or "work exceeds tracked by 6%" rather than implying
         * containment. Null when either side has no data, because "0%" would
         * read as a finding when it only means nothing was recorded.
         */
        agreement: tracked > 0 && work > 0 ? Math.round((work / tracked) * 100) : null,
    };
}

/* ---- Time distribution -------------------------------------------------- */

/*
 * The productive/neutral/unproductive/unrated split, in MILLISECONDS.
 *
 * `summary.statusSummary` is the source rather than `statusDistribution`: the
 * latter is already rounded to whole percentages of totalWorkTime, and four
 * independently rounded percentages rarely add to 100 — the bar would show a
 * gap or overflow its track. Percentages are computed here from the raw
 * milliseconds so the segments always fill it exactly.
 */
export function distribution(summary = {}) {
    const counts = summary.statusSummary ?? {};
    const total = Object.values(counts).reduce((sum, ms) => sum + (ms || 0), 0);

    const rows = ["productive", "neutral", "unproductive", "unrated"].map((key) => ({
        key,
        label: PRODUCTIVITY[key].label,
        tone: PRODUCTIVITY[key].tone,
        ms: counts[key] || 0,
        percent: total ? ((counts[key] || 0) / total) * 100 : 0,
    }));

    return { rows, total };
}

/* ---- Activity timeline -------------------------------------------------- */

export function timeline(summary = {}) {
    return [
        {
            key: "first",
            label: "First Activity",
            at: summary.firstEntry,
            note: "First time entry in this range",
            tone: "emerald",
        },
        {
            key: "last",
            label: "Last Entry",
            at: summary.lastEntry,
            note: "Most recent time entry",
            tone: "brand",
        },
        {
            key: "active",
            label: "Last Active",
            at: summary.lastActive,
            note: "Last keyboard or mouse signal",
            tone: "amber",
        },
    ];
}

/* ---- Usage lists -------------------------------------------------------- */

/*
 * Applications, browsers and websites — the markup's three cards.
 *
 * Browsers are a separate response field, not a slice of usageStats: the
 * backend excludes them from the aggregate on purpose, because a browser's own
 * usage and the usage of the sites inside it are the same seconds counted twice.
 *
 * ONE BAR SCALE ACROSS ALL THREE CARDS. Every bar is drawn against the single
 * longest row anywhere in the three lists, so a two-hour browser and a
 * three-hour editor are visibly two thirds and full — the comparison a reader
 * makes across the cards. Scaling each card to its own total instead makes the
 * top row of every card full-width, which says "this is the biggest here" while
 * looking like "this is the biggest". The markup divided by the day's tracked
 * total, which is nearly the same idea but wrong for browsers: they are
 * excluded from that total, so their bars were drawn as a share of a number
 * they are not part of.
 */
export function usageLists({ usageStats = [], browsers = [] }) {
    const collect = (items, nameOf, kind) =>
        items
            .map((i) => ({
                key: `${kind}:${nameOf(i)}`,
                name: nameOf(i),
                ms: i.totalTime || 0,
                productivityStatus: i.productivityStatus || "unrated",
            }))
            .sort((a, b) => b.ms - a.ms);

    const applications = collect(
        usageStats.filter((u) => u.type === "application"),
        (i) => i.name || "Unknown",
        "application",
    );
    const browserRows = collect(browsers, (i) => i.name || "Unknown", "browser");
    const websites = collect(
        usageStats.filter((u) => u.type === "website" || u.type === "domain"),
        (i) => i.domain || i.name || "Unknown",
        "website",
    );

    const scale = Math.max(0, ...[...applications, ...browserRows, ...websites].map((r) => r.ms));

    const finish = (rows) => ({
        total: rows.reduce((sum, r) => sum + r.ms, 0),
        rows: rows.map((r) => ({ ...r, percent: scale ? (r.ms / scale) * 100 : 0 })),
    });

    return {
        applications: finish(applications),
        browsers: finish(browserRows),
        websites: finish(websites),
    };
}

/* ---- Time entries ------------------------------------------------------- */

/**
 * A row per time entry.
 *
 * `productivityScore` and `productivityBreakdown` are written when an entry
 * ENDS (productivityService, called from endTimeEntry / updateTimeEntry / the
 * stale sweep), so an ongoing entry legitimately has neither. It gets `null`
 * rather than 0 — a running session is not an unproductive one.
 */
export function timeEntryRows(entries = []) {
    return entries.map((e) => {
        const ongoing = !e.endTime;
        const breakdown = e.productivityBreakdown ?? {};
        const spread = ["productive", "neutral", "unproductive", "unrated"].map((key) => ({
            key,
            tone: PRODUCTIVITY[key].tone,
            ms: breakdown[key] || 0,
        }));
        const spreadTotal = spread.reduce((sum, s) => sum + s.ms, 0);

        return {
            id: e._id ?? e.id,
            startTime: e.startTime,
            endTime: e.endTime ?? null,
            ongoing,
            duration: e.duration || 0,
            // Populated by the API; a plain id means an older response, and a
            // hex string is worse than an honest dash.
            project: e.project && typeof e.project === "object" ? e.project.name : null,
            note: e.note || "",
            isManual: Boolean(e.isManual),
            score: ongoing ? null : (e.productivityScore ?? null),
            spread: spread.map((s) => ({
                ...s,
                percent: spreadTotal ? (s.ms / spreadTotal) * 100 : 0,
            })),
            spreadTotal,
        };
    });
}

export const totalOf = (rows, key = "duration") => rows.reduce((sum, r) => sum + (r[key] || 0), 0);

/* ---- Idle times --------------------------------------------------------- */

/*
 * IdleTime stores organization, user, timeEntry, startTime, endTime and
 * duration — and nothing else. The markup's table has "Reason" and "Status
 * (included / ignored)" columns; neither field exists, and neither is derivable,
 * so they are not shown. An invented "No activity detected" in every row would
 * look like data.
 *
 * What IS real and worth a column: whether the period has closed, and which
 * time entry it interrupted.
 */
export function idleRows(idleTimes = []) {
    return idleTimes.map((i) => ({
        id: i._id ?? i.id,
        startTime: i.startTime,
        endTime: i.endTime ?? null,
        ongoing: !i.endTime,
        duration: i.duration || 0,
        timeEntry: typeof i.timeEntry === "object" ? (i.timeEntry?._id ?? null) : (i.timeEntry ?? null),
    }));
}

/* ---- Work-hours trend --------------------------------------------------- */

/** The API's trend points → the bar chart's shape. */
export function trendSeries(trend = [], granularity = "day") {
    return trend.map((point) => ({
        label: point.label ?? point.date ?? "",
        /*
         * `totalHours` is what the endpoint sends — see getWorkHoursTrend, which
         * emits { date, bucketKey, label, workTimeMs, totalHours, entryCount }.
         *
         * This read `point.hours ?? point.duration`, neither of which exists, so
         * every bar was 0 and the chart looked like a day with no work in it.
         * The dashboard reads totalHours and was right all along; nothing
         * compared the two, and the test fixture here had been written to the
         * same invented shape, so it passed while the page showed nothing.
         *
         * workTimeMs is the fallback because it is the same number before
         * rounding, and it is the field to keep if totalHours is ever dropped.
         */
        value: Number(point.totalHours ?? (point.workTimeMs ?? 0) / HOUR) || 0,
        granularity,
    }));
}

/* ---- Desktop app overrides ---------------------------------------------- */

/*
 * The six per-user overrides, exactly the fields User.settings carries and
 * userController.updateUser accepts. Anything not in this list would be
 * silently dropped by the API, so the form would report success and change
 * nothing.
 *
 * `screenshotInterval` is the odd one: it is a NUMBER on the model with null
 * meaning inherit, while the other five are strings with an "inherit" member.
 * The select still offers "inherit" and the conversion happens on submit.
 */
export const OVERRIDES = [
    {
        key: "screenshotResolution",
        label: "Screenshot Quality",
        description: "Resolution the desktop app captures at. Higher quality means larger uploads.",
        options: [
            ["inherit", "Inherit from organization"],
            ["low", "Low — 854×480"],
            ["medium", "Medium — 1280×720"],
            ["high", "High — 1600×900"],
            ["full", "Full — 1920×1080"],
        ],
    },
    {
        key: "showTrayIcon",
        label: "Tray Icon",
        description: "Whether the tracker's icon is visible in the system tray / menu bar.",
        options: [
            ["inherit", "Inherit from organization"],
            ["show", "Show tray icon"],
            ["hide", "Hide tray icon"],
        ],
    },
    {
        key: "screenshotInterval",
        label: "Screenshot Interval",
        description: "How often a screenshot is captured while tracking is active.",
        numeric: true,
        options: [
            ["inherit", "Inherit from organization"],
            // The model's minimum is 6,000 ms — anything below it would be
            // rejected on save, so it is not offered.
            ["10000", "10 seconds"],
            ["30000", "30 seconds"],
            ["60000", "1 minute"],
            ["120000", "2 minutes"],
            ["300000", "5 minutes"],
            ["600000", "10 minutes"],
        ],
    },
    {
        key: "trackKeyLog",
        label: "Key Log",
        description:
            "Records every key pressed, so the Key Log tab can reconstruct what was typed — including passwords.",
        options: [
            ["inherit", "Inherit from organization"],
            ["yes", "Enable key log"],
            ["no", "Disable key log"],
        ],
    },
    {
        key: "clipboardCapture",
        label: "Clipboard",
        description:
            "Captures copy/paste text alongside each screenshot. Images store a size marker only.",
        // Gated by the SuperAdmin entitlement: with clipboardCaptureAllowed
        // false, the desktop ignores this setting whatever it says.
        entitlement: "clipboardCaptureAllowed",
        entitlementNote:
            "Clipboard capture is not enabled for this organization, so this override would have no effect.",
        options: [
            ["inherit", "Inherit from organization"],
            ["enabled", "Enable clipboard"],
            ["disabled", "Disable clipboard"],
        ],
    },
    {
        key: "fileLogging",
        label: "File Logging",
        description:
            "Writes rotating diagnostic logs on the device. Off by default; used for debugging stalls.",
        options: [
            ["inherit", "Inherit from organization"],
            ["enabled", "Enable file logging"],
            ["disabled", "Disable file logging"],
        ],
    },
];

/** User.settings → the form's string values. */
export function overrideValues(settings = {}) {
    const values = {};
    for (const field of OVERRIDES) {
        const raw = settings?.[field.key];
        values[field.key] = field.numeric
            ? raw == null
                ? "inherit"
                : String(raw)
            : (raw ?? "inherit");
    }
    return values;
}

/** Only what CHANGED, converted back to what the API stores. */
export function overridePatch(values, settings = {}) {
    const current = overrideValues(settings);
    const patch = {};

    for (const field of OVERRIDES) {
        if (values[field.key] === current[field.key]) continue;
        patch[field.key] = field.numeric
            ? values[field.key] === "inherit"
                ? null
                : Number(values[field.key])
            : values[field.key];
    }

    return patch;
}

/* ---- Feature gates ------------------------------------------------------ */

/*
 * Whether the Key Log and Clipboard views will actually have anything in them,
 * resolved the same way the desktop resolves them — per-user override first,
 * organization setting second, entitlement over the top for clipboard.
 *
 * Used to explain an empty tab rather than to hide it: "nobody typed" and "the
 * feature is off" look identical on screen, and only one of them is worth
 * acting on.
 */
export function featureGates(orgSettings = {}, userSettings = {}, organization = {}) {
    /*
     * WHICH gate is off matters as much as whether one is.
     *
     * An empty Key Log panel that says "off for this organization" sends an
     * admin to the organization settings — where they find it switched ON,
     * conclude the feature is broken, and report a bug. That is exactly what
     * happened: the organization had trackKeyLog true and the PER-USER override
     * was "no", which is the gate that was actually closed.
     *
     * So each gate reports its reason, and the message names the switch the
     * reader has to go and change.
     */
    const keyLogOverride = userSettings?.trackKeyLog ?? "inherit";
    const keyLog =
        keyLogOverride === "inherit" ? Boolean(orgSettings?.trackKeyLog) : keyLogOverride === "yes";

    const keyLogReason = keyLog ? "on" : keyLogOverride === "no" ? "user-off" : "org-off";

    const entitled = Boolean(organization?.clipboardCaptureAllowed);
    const clipOverride = userSettings?.clipboardCapture ?? "inherit";
    const clipboard =
        entitled &&
        (clipOverride === "inherit"
            ? Boolean(orgSettings?.clipboardEnabled)
            : clipOverride === "enabled");

    const clipboardReason = clipboard
        ? "on"
        : !entitled
          ? "not-entitled"
          : clipOverride === "disabled"
            ? "user-off"
            : "org-off";

    return { keyLog, clipboard, clipboardEntitled: entitled, keyLogReason, clipboardReason };
}

/*
 * One sentence per gate state, in one place, so the screenshot viewer, the Key
 * Log tab and the organization-wide Screenshots page cannot drift into telling
 * a reader three different stories about the same switch.
 *
 * `gates` may be null — the caller could not resolve them (an older response,
 * or a user whose record is not loaded). Then the honest thing is to say the
 * interval was empty and list both possibilities rather than pick one.
 */
export function gateMessage(kind, gates, { subject = "this user" } = {}) {
    const reason = gates ? (kind === "keys" ? gates.keyLogReason : gates.clipboardReason) : null;
    const feature = kind === "keys" ? "Key logging" : "Clipboard capture";

    if (reason === "on" || reason === null) {
        return {
            title:
                kind === "keys"
                    ? "No keystrokes recorded in this interval"
                    : "No clipboard activity in this interval",
            hint:
                reason === "on"
                    ? undefined
                    : `${feature} may also be switched off for ${subject}.`,
        };
    }

    if (reason === "not-entitled") {
        return {
            title: "Clipboard capture is not enabled for this organization",
            hint: "A Super Admin has to grant the organization clipboard capture before it can be switched on.",
        };
    }

    if (reason === "user-off") {
        return {
            title: `${feature} is switched off for ${subject}`,
            hint: `The organization has it on — this is the per-user override on ${subject}'s page, under Desktop App Overrides.`,
        };
    }

    return {
        title: `${feature} is off for this organization`,
        hint: "Turn it on in the organization's settings, or override it for one person under Desktop App Overrides.",
    };
}

/* ---- Presence ----------------------------------------------------------- */

/*
 * "Active now" — the same five-minute window presenceOf() uses on the
 * dashboard, so the two pages never disagree about who is at their desk.
 *
 * `now` is a parameter with a default, matching lib/dashboard.js: it keeps the
 * clock read out of component render (where a value that changes between
 * renders is a purity bug) and makes the rule testable without mocking time.
 */
export const ONLINE_MS = 5 * 60_000;

export function isOnline(lastActive, now = Date.now()) {
    if (!lastActive) return false;
    const at = new Date(lastActive).getTime();
    return Number.isFinite(at) && now - at < ONLINE_MS;
}

/* ---- Date presets ------------------------------------------------------- */

export const RANGE_PRESETS = {
    today: { label: "Today", days: 0 },
    "7d": { label: "7 days", days: 6 },
    "30d": { label: "30 days", days: 29 },
};
